import { useState } from "react";
import { Download } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { lpoApi, procPdfApi } from "../../api";
import { ProcError, ProcField, ProcForm, ProcGrid, ProcLoading, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, useMutation } from "../../hooks";

function calcNet(line: { quantity: number; rate: number; discountPct?: number | null }): number {
  return line.quantity * line.rate * (1 - (line.discountPct ?? 0) / 100);
}

export default function LPOViewPage() {
  const { id } = useParams<{ id: string }>();
  const [downloading, setDownloading] = useState(false);

  const { data: lpo, loading, error, reload } = useAsyncData(
    () => lpoApi.get(Number(id)),
    [id],
  );

  const { mutate: doAuthorize, loading: authorizing, error: authorizeErr } = useMutation(() =>
    lpoApi.authorize(Number(id)).then(() => reload()),
  );

  const { mutate: doSend, loading: sending, error: sendErr } = useMutation(() =>
    lpoApi.send(Number(id)).then(() => reload()),
  );

  async function downloadPdf() {
    if (!lpo) return;
    setDownloading(true);
    try {
      const blob = await procPdfApi.lpo(Number(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `lpo-${lpo.serialNo ?? id}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } finally { setDownloading(false); }
  }

  if (loading) return <ProcLoading />;
  if (error) return <ProcError message={error} />;
  if (!lpo) return null;

  const subtotal = lpo.subtotal ?? lpo.lines.reduce((s, l) => s + calcNet(l), 0);
  const vat = lpo.vat ?? subtotal * 0.16;
  const total = lpo.total ?? subtotal + vat;
  const actionErr = authorizeErr || sendErr;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Link to="/procurement/lpos" className="text-sm text-emerald-700 hover:underline">&larr; Back to LPOs</Link>
        <button onClick={downloadPdf} disabled={downloading}
          className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <Download className="h-4 w-4" />
          {downloading ? "Downloading…" : "Download PDF"}
        </button>
      </div>

      <ProcForm formNo="PROC-FRM-04" title="Local Purchase Order" serialNo={lpo.serialNo} date={lpo.date}>
        <ProcSection title="Order Details">
          <ProcGrid>
            <ProcField label="Supplier" value={lpo.supplierName} />
            <ProcField label="Delivery Date" value={lpo.deliveryDate ? new Date(lpo.deliveryDate).toLocaleDateString("en-KE") : "—"} />
            <ProcField label="Payment Terms" value={lpo.paymentTerms} />
            <ProcField label="Status" value={<ProcStatusBadge value={lpo.status} />} />
            <ProcField label="Supplier Address" value={lpo.supplierAddress} span2 />
          </ProcGrid>
        </ProcSection>

        <ProcSection title="Line Items">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Item Spec</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Disc%</th>
                  <th className="px-3 py-2 text-right">Net Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lpo.lines.map((l) => (
                  <tr key={l.id ?? l.lineOrder}>
                    <td className="px-3 py-2 text-slate-400">{l.lineOrder}</td>
                    <td className="px-3 py-2">{l.itemSpec}</td>
                    <td className="px-3 py-2 text-slate-500">{l.unitMeasure}</td>
                    <td className="px-3 py-2 text-right">{l.quantity}</td>
                    <td className="px-3 py-2 text-right">{l.rate.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{l.discountPct ?? 0}%</td>
                    <td className="px-3 py-2 text-right font-semibold">{calcNet(l).toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-semibold text-sm">
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-right text-slate-600">Subtotal:</td>
                  <td className="px-3 py-2 text-right">{subtotal.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-right text-slate-600">VAT (16%):</td>
                  <td className="px-3 py-2 text-right">{vat.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                </tr>
                <tr>
                  <td colSpan={6} className="px-3 py-2 text-right text-emerald-700 font-bold">Total:</td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-bold">{total.toLocaleString("en-KE", { style: "currency", currency: "KES" })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </ProcSection>

        {(lpo.status === "DRAFT" || lpo.status === "AUTHORIZED") && (
          <ProcSection title="Actions">
            {actionErr && <ProcError message={actionErr} />}
            <div className="flex flex-wrap gap-3">
              {lpo.status === "DRAFT" && (
                <button type="button" onClick={() => doAuthorize()} disabled={authorizing} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                  {authorizing ? "Authorizing…" : "Authorize LPO"}
                </button>
              )}
              {lpo.status === "AUTHORIZED" && (
                <button type="button" onClick={() => doSend()} disabled={sending} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
                  {sending ? "Sending…" : "Send to Supplier"}
                </button>
              )}
            </div>
          </ProcSection>
        )}
      </ProcForm>
    </div>
  );
}
